import { useState } from 'react';
import { money, utcTime } from '../format';
import type { SpendPoint } from '../types';

interface SpendChartProps {
  points: SpendPoint[];
  asOf: string | null;
  /** Which series to show. Defaults to both. */
  series?: ('byok' | 'credits')[];
  /** Days range (triggers a refetch when changed via the selector). */
  days: number;
  onDaysChange?: (days: number) => void;
}

/**
 * Inline SVG spend time-series chart. Displays BYOK and credits as separate series
 * (per M6 contract: BYOK-only deployment means byok_usage is primary, but show both).
 * No charting library; follows dataviz skill guidance (thin marks, 2px lines, legend
 * for 2+ series, accessible, light/dark support).
 */
export function SpendChart({ points, asOf, series = ['byok', 'credits'], days, onDaysChange }: SpendChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // Empty state: no data or all values null
  const hasData = points.length > 0 && points.some((p) => p.byok_usage !== null || p.credits_usage !== null);

  if (!hasData) {
    return (
      <div className="spend-chart-empty">
        <p className="muted">No spend history yet</p>
      </div>
    );
  }

  // Chart dimensions
  const width = 600;
  const height = 200;
  const marginTop = 20;
  const marginRight = 20;
  const marginBottom = 40;
  const marginLeft = 60;
  const plotWidth = width - marginLeft - marginRight;
  const plotHeight = height - marginTop - marginBottom;

  // Data ranges
  const maxByok = Math.max(0, ...points.map((p) => p.byok_usage ?? 0));
  const maxCredits = Math.max(0, ...points.map((p) => p.credits_usage ?? 0));
  const yMax = Math.max(maxByok, maxCredits) * 1.1; // 10% headroom
  const yScale = yMax > 0 ? plotHeight / yMax : 0;
  const xStep = plotWidth / Math.max(1, points.length - 1);

  // Series colors: per dataviz palette (light mode) - categorical slots 1 (blue) and 2 (orange)
  // Dark mode versions defined in CSS vars
  const showByok = series.includes('byok');
  const showCredits = series.includes('credits');
  const seriesCount = (showByok ? 1 : 0) + (showCredits ? 1 : 0);

  // Build line paths
  const byokPath = showByok
    ? points
        .map((p, i) => {
          if (p.byok_usage === null) return null;
          const x = marginLeft + i * xStep;
          const y = marginTop + plotHeight - p.byok_usage * yScale;
          return `${i === 0 ? 'M' : 'L'}${x},${y}`;
        })
        .filter(Boolean)
        .join(' ')
    : '';

  const creditsPath = showCredits
    ? points
        .map((p, i) => {
          if (p.credits_usage === null) return null;
          const x = marginLeft + i * xStep;
          const y = marginTop + plotHeight - p.credits_usage * yScale;
          return `${i === 0 ? 'M' : 'L'}${x},${y}`;
        })
        .filter(Boolean)
        .join(' ')
    : '';

  // Y-axis ticks (0, mid, max)
  const yTicks = [0, yMax / 2, yMax];

  // Hover tooltip
  const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div className="spend-chart">
      <div className="spend-chart-controls">
        <div className="spend-chart-freshness">
          {asOf ? (
            <span className="muted small">
              Daily history (as of {utcTime(asOf)})
            </span>
          ) : (
            <span className="muted small">Daily history (no cached data)</span>
          )}
        </div>
        {onDaysChange && (
          <div className="spend-chart-days-selector">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => onDaysChange(d)}
                className={days === d ? 'active' : ''}
                disabled={days === d}
              >
                {d}d
              </button>
            ))}
          </div>
        )}
      </div>

      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="spend-chart-svg"
        role="img"
        aria-label={`Spend time-series chart showing ${seriesCount} series over ${days} days`}
      >
        <title>Daily spend history</title>

        {/* Y-axis gridlines (recessive hairlines) */}
        {yTicks.map((tick) => {
          const y = marginTop + plotHeight - tick * yScale;
          return (
            <line
              key={tick}
              x1={marginLeft}
              y1={y}
              x2={marginLeft + plotWidth}
              y2={y}
              stroke="var(--chart-grid)"
              strokeWidth="1"
            />
          );
        })}

        {/* Y-axis labels */}
        {yTicks.map((tick) => {
          const y = marginTop + plotHeight - tick * yScale;
          return (
            <text
              key={`label-${tick}`}
              x={marginLeft - 8}
              y={y}
              textAnchor="end"
              alignmentBaseline="middle"
              fill="var(--chart-text-secondary)"
              fontSize="11"
            >
              {money(tick, '$0')}
            </text>
          );
        })}

        {/* X-axis (baseline) */}
        <line
          x1={marginLeft}
          y1={marginTop + plotHeight}
          x2={marginLeft + plotWidth}
          y2={marginTop + plotHeight}
          stroke="var(--chart-grid)"
          strokeWidth="1"
        />

        {/* X-axis labels (first, last, and midpoint if days > 14) */}
        {points.length > 0 && (
          <>
            <text
              x={marginLeft}
              y={marginTop + plotHeight + 16}
              textAnchor="start"
              fill="var(--chart-text-secondary)"
              fontSize="11"
            >
              {points[0]?.day ?? ''}
            </text>
            {points.length > 14 && points.length > 2 && (
              <text
                x={marginLeft + plotWidth / 2}
                y={marginTop + plotHeight + 16}
                textAnchor="middle"
                fill="var(--chart-text-secondary)"
                fontSize="11"
              >
                {points[Math.floor(points.length / 2)]?.day ?? ''}
              </text>
            )}
            <text
              x={marginLeft + plotWidth}
              y={marginTop + plotHeight + 16}
              textAnchor="end"
              fill="var(--chart-text-secondary)"
              fontSize="11"
            >
              {points[points.length - 1]?.day ?? ''}
            </text>
          </>
        )}

        {/* BYOK line (series 1, blue) */}
        {showByok && byokPath && (
          <path
            d={byokPath}
            stroke="var(--chart-series-1)"
            strokeWidth="2"
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Credits line (series 2, orange) */}
        {showCredits && creditsPath && (
          <path
            d={creditsPath}
            stroke="var(--chart-series-2)"
            strokeWidth="2"
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Hover markers with 2px surface ring */}
        {points.map((p, i) => {
          const x = marginLeft + i * xStep;
          const markers = [];

          if (showByok && p.byok_usage !== null) {
            const y = marginTop + plotHeight - p.byok_usage * yScale;
            markers.push(
              <g key={`byok-${i}`}>
                <circle cx={x} cy={y} r="5" fill="var(--chart-surface)" />
                <circle cx={x} cy={y} r="3" fill="var(--chart-series-1)" />
              </g>
            );
          }

          if (showCredits && p.credits_usage !== null) {
            const y = marginTop + plotHeight - p.credits_usage * yScale;
            markers.push(
              <g key={`credits-${i}`}>
                <circle cx={x} cy={y} r="5" fill="var(--chart-surface)" />
                <circle cx={x} cy={y} r="3" fill="var(--chart-series-2)" />
              </g>
            );
          }

          // Hover hit target (larger than the marker)
          markers.push(
            <rect
              key={`hit-${i}`}
              x={x - 10}
              y={marginTop}
              width="20"
              height={plotHeight}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
              style={{ cursor: 'crosshair' }}
            />
          );

          return markers;
        })}

        {/* Hover tooltip */}
        {hoverPoint && hoverIndex !== null && (
          <g>
            {/* Crosshair vertical line */}
            <line
              x1={marginLeft + hoverIndex * xStep}
              y1={marginTop}
              x2={marginLeft + hoverIndex * xStep}
              y2={marginTop + plotHeight}
              stroke="var(--chart-text-secondary)"
              strokeWidth="1"
              strokeDasharray="2,2"
              opacity="0.5"
            />
            {/* Tooltip box */}
            <foreignObject
              x={marginLeft + hoverIndex * xStep + 10}
              y={marginTop + 10}
              width="150"
              height="80"
            >
              <div className="spend-chart-tooltip">
                <div className="spend-chart-tooltip-date">{hoverPoint.day}</div>
                {showByok && (
                  <div className="spend-chart-tooltip-row">
                    <span className="spend-chart-tooltip-label" style={{ color: 'var(--chart-series-1)' }}>
                      ● BYOK
                    </span>
                    <span className="spend-chart-tooltip-value">{money(hoverPoint.byok_usage, '—')}</span>
                  </div>
                )}
                {showCredits && (
                  <div className="spend-chart-tooltip-row">
                    <span className="spend-chart-tooltip-label" style={{ color: 'var(--chart-series-2)' }}>
                      ● Credits
                    </span>
                    <span className="spend-chart-tooltip-value">{money(hoverPoint.credits_usage, '—')}</span>
                  </div>
                )}
              </div>
            </foreignObject>
          </g>
        )}
      </svg>

      {/* Legend (always present for 2+ series) */}
      {seriesCount >= 2 && (
        <div className="spend-chart-legend">
          {showByok && (
            <div className="spend-chart-legend-item">
              <span className="spend-chart-legend-marker" style={{ backgroundColor: 'var(--chart-series-1)' }} />
              <span className="spend-chart-legend-label">BYOK usage</span>
            </div>
          )}
          {showCredits && (
            <div className="spend-chart-legend-item">
              <span className="spend-chart-legend-marker" style={{ backgroundColor: 'var(--chart-series-2)' }} />
              <span className="spend-chart-legend-label">Credits usage</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
